import React, { useState, createRef } from 'react';
import { Field, Form } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { FORM_ERROR } from 'final-form';
import Bluebird from 'bluebird';
import { Grid, Paper, Tooltip, Typography, TextField as TextFieldMaterial } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import snackbarHelper from '../helpers/snackbarHelper';
import * as errorsHelper from '../helpers/errors';
import SubmitWithSendEmailConfirmation from './SubmitWithSendEmailConfirmation';
import OverlayLoader from './OverlayLoader';
import routes from '../routes';
import { Colors } from '../assets/theme/Colors';
import FormGrid from './Common/FormGrid';
import { getFormFieldError, isFormFieldInvalid } from '../helpers/validators';

const TITLE_MAX_CHARACTERS_LIMIT = 65;
const BODY_MAX_CHARACTERS_LIMIT = 240;

const paperSx = {
  my: 1,
  padding: { xs: 1, sm: 2 },
};

const buttonSx = {
  marginTop: 1,
};

const SendPushForm = ({ recipients, submitCallback, groupName }) => {
  const formRef = createRef();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const validate = (values) => {
    const errors = {};

    if (!values.title) {
      errors.title = 'Required';
    }

    if (!values.body) {
      errors.body = 'Required';
    }

    if (recipients.length <= 0) {
      errors.recipients = 'Required';
    }

    return errors;
  };

  const submit = (values) =>
    Bluebird.try(() => {
      setLoading(true);

      return submitCallback({
        title: values.title,
        body: values.body,
        redirect_url: values.redirect_url || null,
        users_ids: recipients.map((r) => r.id),
      });
    })
      .then(() => {
        history.push(routes.rescuers);

        snackbarHelper.success(`Push sent successfully to ${recipients.length} recipient(s)!`);
      })
      .catch((err) => {
        errorsHelper.handleError(err);

        return {
          [FORM_ERROR]: 'POST failed',
        };
      });

  return (
    <OverlayLoader isLoading={loading}>
      <Paper sx={paperSx} data-testid="push-recipients-paper">
        <Typography variant="h6">
          {groupName ? `Recipients (${recipients.length}); ${groupName}:` : `Recipients (${recipients.length}):`}

          <Tooltip
            arrow
            placement="top"
            title="List of selected users. Only those with a registered device will receive this push."
          >
            <HelpIcon htmlColor={Colors.blue.main} style={{ position: 'relative', top: 5, marginLeft: 5 }} />
          </Tooltip>
        </Typography>

        {recipients.length > 0 ? (
          <ul>
            {recipients.slice(0, 10).map((r) => (
              <li key={r.id}>{`${r.firstname} ${r.lastname}`}</li>
            ))}

            {recipients.length > 10 && (
              <li>
                <em>{`+${recipients.length - 10} more recipients`}</em>
              </li>
            )}
          </ul>
        ) : (
          <Typography data-testid="no-recipients-help" color="error">
            You don't have any recipients selected therefore you will not be able to send the push.
          </Typography>
        )}
      </Paper>

      <Paper sx={paperSx}>
        <Form
          onSubmit={submit}
          validate={validate}
          render={({ handleSubmit, submitting, pristine, invalid, dirtySinceLastSubmit }) => (
            <form onSubmit={handleSubmit} ref={formRef}>
              <FormGrid>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Field
                      name="title"
                      render={({ input, meta }) => {
                        const showError = isFormFieldInvalid(meta);

                        return (
                          <TextFieldMaterial
                            variant="standard"
                            InputProps={{
                              disabled: recipients.length === 0,
                            }}
                            fullWidth
                            required
                            type="text"
                            label="Push Title"
                            inputProps={{
                              maxLength: TITLE_MAX_CHARACTERS_LIMIT,
                            }}
                            error={showError}
                            helperText={
                              showError
                                ? getFormFieldError(meta)
                                : `Used ${input.value.length} out of ${TITLE_MAX_CHARACTERS_LIMIT} characters`
                            }
                            {...input}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="body"
                      render={({ input, meta }) => {
                        const showError = isFormFieldInvalid(meta);

                        return (
                          <TextFieldMaterial
                            variant="standard"
                            InputProps={{
                              disabled: recipients.length === 0,
                            }}
                            fullWidth
                            maxRows={10}
                            multiline
                            required
                            type="text"
                            label="Push Content"
                            inputProps={{
                              maxLength: BODY_MAX_CHARACTERS_LIMIT,
                            }}
                            error={showError}
                            helperText={
                              showError
                                ? getFormFieldError(meta)
                                : `Used ${input.value.length} out of ${BODY_MAX_CHARACTERS_LIMIT} characters`
                            }
                            {...input}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="redirect_url"
                      render={({ input, meta }) => {
                        const showError = isFormFieldInvalid(meta);

                        return (
                          <TextFieldMaterial
                            variant="standard"
                            InputProps={{
                              disabled: recipients.length === 0,
                            }}
                            fullWidth
                            type="text"
                            label="Deep link (optional)"
                            error={showError}
                            helperText={
                              showError
                                ? getFormFieldError(meta)
                                : 'Where tapping the push should take the user. Defaults to the app dashboard.'
                            }
                            {...input}
                          />
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SubmitWithSendEmailConfirmation
                      title="Heads up!"
                      message={`Do you really want to send push out to ${recipients.length} recipient(s)?`}
                      confirmButtonLabel="Send Push"
                      label="Send Push"
                      showConfirmation
                      disabled={pristine || (invalid && !dirtySinceLastSubmit) || submitting}
                      sx={buttonSx}
                      onButtonNotSend={() => true}
                      onButtonSend={() => {
                        formRef.current.dispatchEvent(
                          new Event('submit', {
                            bubbles: true,
                            cancelable: true,
                          })
                        );
                      }}
                      data-testid="send-push-submit-button"
                    />
                  </Grid>
                </Grid>
              </FormGrid>
            </form>
          )}
        />
      </Paper>
    </OverlayLoader>
  );
};

export default SendPushForm;
